import type { QueryClient } from "@tanstack/react-query";

/** A request whose outcome is unknown: resent exactly (same variables, same key) until explicitly discarded. */
export interface Lock {
  vars: unknown;
  key: string;
  /**
   * The form values that produced `vars` (review RW1, RW2): a reopened dialog shows exactly the locked request, read-only,
   * so what "Try again (same request)" resends is what the person sees.
   */
  form?: unknown;
}

/**
 * Locks live with the module's QueryClient, not with a dialog (review R2-1): closing and reopening a dialog restores
 * the lock and "Try again (same request)", so no new key is ever made silently. Keyed per person and intent
 * (operation + subject, e.g. `demand:<lease_id>`); only "Discard and start again" drops one.
 */
const STORES = new WeakMap<QueryClient, Map<string, Lock>>();

export function lockStore(qc: QueryClient): Map<string, Lock> {
  let s = STORES.get(qc);
  if (!s) {
    s = new Map();
    STORES.set(qc, s);
  }
  return s;
}

/** Drop every lock of a sign-in kind (a FER local session that ended: nothing it held survives it; review FW7). */
export function dropLocksOf(qc: QueryClient, auth: string): void {
  const s = lockStore(qc);
  for (const k of [...s.keys()]) if (k.split("|")[1] === auth) s.delete(k);
}
